import React, { useState, useMemo } from 'react';
import './ExecutionCommon.css';
import CaseEvidenceFrameView from './CaseEvidenceFrameView';
import ViolationsReportView from './ViolationsReportView';
import AggregateDecisionCard from './AggregateDecisionCard';
import ExecutionInsightDetail from '../../UserHome/ExecutionInsightDetail';
import ActivationGraph from '../../explainability/ActivationGraph';

function NodeResultDetail({ node, result, historyEvents, onClose, loading, graphId }) {
  const [expandedSections, setExpandedSections] = useState({
    inputs: true,
    outputs: true,
    config: false,
    prompt: false,
    response: false,
    snn: false,
    case_evidence: true,
    violations: true,
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Get status from result
  const status = result?.status || 'pending';
  const isLLM = node?.execution_type === 'llm';
  const isSNN = node?.execution_type === 'snn_pipeline';
  const isCaseExtractor = node?.execution_type === 'case_extractor';
  const isChainReasoner = node?.execution_type === 'chain_reasoner';
  const isCrossLink = node?.execution_type === 'cross_link';
  const isCortexActivation = node?.node_id === 'cortex_activation';
  const isDynamicCortexExecutor = node?.node_id === 'capability_engines' && !!result?.outputs_produced?.dynamic_execution;
  const crossLink = result?.metadata?.cross_link || null;

  const snnInsight = useMemo(() => {
    if (!isSNN || !result?.snn_execution_trace) return null;
    return {
      execution_trace: result.snn_execution_trace,
      prompt_expansions: result.snn_prompt_expansions,
      score: result.snn_score,
      natural_language_answer: result.snn_answer,
      query: result.snn_query,
    };
  }, [isSNN, result]);

  if (!node) return null;

  // Locate the case_evidence_frame on the result regardless of where the
  // executor stashed it (top-level field, outputs_produced, or
  // case_evidence_frame field added by the categorical executor).
  const caseEvidenceFrame =
    result?.case_evidence_frame
    || result?.outputs_produced?.case_evidence_frame
    || null;
  const violationsReport =
    result?.violations_report
    || result?.outputs_produced?.violations_report
    || null;

  return (
    <div className="node-result-detail">
      {/* Header */}
      <div className="detail-header">
        <div className="header-info">
          <h3>{node.name}</h3>
          <span className={`status-badge ${status}`}>{status.toUpperCase()}</span>
        </div>
        <button className="close-btn" onClick={onClose}>×</button>
      </div>

      {loading && !result && (
        <div className="detail-loading">
          <div className="spinner"></div>
          <span>Loading node details...</span>
        </div>
      )}

      {/* Node Info */}
      <div className="detail-section">
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Node ID</span>
            <span className="info-value">{node.node_id}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Type</span>
            <span className="info-value">{node.execution_type}</span>
          </div>
          {result?.duration_ms && (
            <div className="info-item">
              <span className="info-label">Duration</span>
              <span className="info-value">{result.duration_ms}ms</span>
            </div>
          )}
          {result?.retry_count > 0 && (
            <div className="info-item">
              <span className="info-label">Retries</span>
              <span className="info-value">{result.retry_count}</span>
            </div>
          )}
        </div>
      </div>

      {/* Node Configuration */}
      {(node.snn_config || node.python_config || node.llm_config || node.http_config) && (
        <div className="detail-section collapsible">
          <div className="section-header" onClick={() => toggleSection('config')}>
            <h4>Configuration</h4>
            <span className="toggle-icon">{expandedSections.config ? '▼' : '▶'}</span>
          </div>
          {expandedSections.config && (
            <div className="config-section">
              {node.snn_config && (
                <div className="config-grid">
                  {node.snn_config.graph_name && (
                    <div className="info-item">
                      <span className="info-label">Graph</span>
                      <span className="info-value">{node.snn_config.graph_name}</span>
                    </div>
                  )}
                  {node.snn_config.graph_id && (
                    <div className="info-item">
                      <span className="info-label">Graph ID</span>
                      <span className="info-value" style={{fontSize: '11px', fontFamily: 'monospace'}}>{node.snn_config.graph_id}</span>
                    </div>
                  )}
                  {node.snn_config.snn_execution_mode && (
                    <div className="info-item">
                      <span className="info-label">Execution Mode</span>
                      <span className="info-value">{node.snn_config.snn_execution_mode}</span>
                    </div>
                  )}
                  {node.snn_config.similarity_top_k && (
                    <div className="info-item">
                      <span className="info-label">Top-K</span>
                      <span className="info-value">{node.snn_config.similarity_top_k}</span>
                    </div>
                  )}
                  {node.snn_config.max_hops && (
                    <div className="info-item">
                      <span className="info-label">Max Hops</span>
                      <span className="info-value">{node.snn_config.max_hops}</span>
                    </div>
                  )}
                  {node.snn_config.focus_areas?.length > 0 && (
                    <div className="info-item" style={{gridColumn: '1 / -1'}}>
                      <span className="info-label">Focus Areas</span>
                      <span className="info-value">{node.snn_config.focus_areas.join(', ')}</span>
                    </div>
                  )}
                </div>
              )}
              {node.python_config && (
                <div className="config-grid">
                  {node.python_config.entrypoint && (
                    <div className="info-item">
                      <span className="info-label">Entrypoint</span>
                      <span className="info-value">{node.python_config.entrypoint}</span>
                    </div>
                  )}
                  {node.python_config.wall_seconds && (
                    <div className="info-item">
                      <span className="info-label">Timeout</span>
                      <span className="info-value">{node.python_config.wall_seconds}s</span>
                    </div>
                  )}
                  {node.python_config.memory_mb && (
                    <div className="info-item">
                      <span className="info-label">Memory</span>
                      <span className="info-value">{node.python_config.memory_mb} MB</span>
                    </div>
                  )}
                </div>
              )}
              {(node.llm_config || node.http_config) && (
                <pre className="config-json">{JSON.stringify(node.llm_config || node.http_config, null, 2)}</pre>
              )}
            </div>
          )}
        </div>
      )}

      {/* Token Usage (for any LLM-consuming node, including SNN pipeline) */}
      {result && (isLLM || isSNN || (result?.tokens_used > 0)) && (() => {
        const llmUsage = result?.metadata?.llm_usage || null;
        const ov = (llmUsage && llmUsage.overall) || null;
        const promptT = ov ? ov.prompt_tokens : (result.prompt_tokens || 0);
        const completionT = ov ? ov.completion_tokens : (result.completion_tokens || 0);
        const totalT = ov ? ov.total_tokens : (result.tokens_used || 0);
        const cost = ov ? ov.cost_usd : null;
        const callCount = ov ? ov.call_count : null;
        const model = (ov && ov.model) || result.llm_model;
        const ragSavings = result?.metadata?.rag_savings || null;
        const fmtNum = (n) => Number(n || 0).toLocaleString();
        const fmtCost = (c) => {
          const v = Number(c || 0);
          if (v <= 0) return null;
          if (v < 0.0001) return '<$0.0001';
          if (v < 0.01) return `$${v.toFixed(5)}`;
          return `$${v.toFixed(4)}`;
        };
        if (totalT === 0 && !ov && !ragSavings) return null;
        return (
          <div className="detail-section">
            <h4>Token Usage</h4>
            <div className="token-grid">
              <div className="token-item">
                <span className="token-label">Prompt</span>
                <span className="token-value">{fmtNum(promptT)}</span>
              </div>
              <div className="token-item">
                <span className="token-label">Completion</span>
                <span className="token-value">{fmtNum(completionT)}</span>
              </div>
              <div className="token-item total">
                <span className="token-label">Total</span>
                <span className="token-value">{fmtNum(totalT)}</span>
              </div>
              {cost != null && (
                <div className="token-item">
                  <span className="token-label">Cost</span>
                  <span className="token-value">{fmtCost(cost) || '—'}</span>
                </div>
              )}
              {callCount != null && (
                <div className="token-item">
                  <span className="token-label">LLM Calls</span>
                  <span className="token-value">{fmtNum(callCount)}</span>
                </div>
              )}
            </div>
            {model && (
              <div className="model-info">
                Model: <span>{model}</span>
              </div>
            )}
            {ragSavings && ragSavings.savings_pct != null && (
              <div
                className="model-info"
                style={{marginTop: '6px', padding: '6px 10px', background: '#ecfdf5', color: '#047857', borderRadius: 4, border: '1px solid #6ee7b7'}}
                title={`Compared against full-document RAG (${ragSavings.graph_node_count} nodes \u00B7 ~600 tokens each).`}
              >
                Saved <strong>{Number(ragSavings.savings_pct).toFixed(1)}%</strong> vs full-document RAG
                {' '}({fmtNum(ragSavings.savings_tokens)} tokens)
              </div>
            )}
            {llmUsage && llmUsage.per_phase && Object.keys(llmUsage.per_phase).length > 0 && (
              <details style={{marginTop: '8px'}}>
                <summary style={{cursor: 'pointer'}}>Per-phase breakdown</summary>
                <table style={{width: '100%', fontSize: '12px', marginTop: '4px', borderCollapse: 'collapse'}}>
                  <thead>
                    <tr>
                      <th style={{textAlign:'left', padding: '2px 6px'}}>Phase</th>
                      <th style={{textAlign:'right', padding: '2px 6px'}}>Calls</th>
                      <th style={{textAlign:'right', padding: '2px 6px'}}>Tokens</th>
                      <th style={{textAlign:'right', padding: '2px 6px'}}>Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.values(llmUsage.per_phase)
                      .sort((a, b) => (b.total_tokens || 0) - (a.total_tokens || 0))
                      .map((p) => (
                        <tr key={p.phase}>
                          <td style={{padding: '2px 6px'}}>{p.phase}</td>
                          <td style={{textAlign:'right', padding: '2px 6px'}}>{fmtNum(p.call_count)}</td>
                          <td style={{textAlign:'right', padding: '2px 6px'}}>{fmtNum(p.total_tokens)}</td>
                          <td style={{textAlign:'right', padding: '2px 6px'}}>${(p.cost_usd || 0).toFixed(5)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </details>
            )}
          </div>
        );
      })()}

      {/* Cortex Activation Details */}
      {isCortexActivation && result?.outputs_produced?.cortex_result && (() => {
        const cr = result.outputs_produced.cortex_result;
        const engines = result.outputs_produced.engines_to_run || [];
        const route = result.outputs_produced.dominant_route || cr.dominant_route || 'N/A';
        const caps = cr.ranked_capabilities || {};
        const sortedCaps = Object.entries(caps).sort((a, b) => b[1] - a[1]);
        const maxScore = sortedCaps.length > 0 ? sortedCaps[0][1] : 1;

        return (
          <div className="detail-section cortex-activation-detail">
            <h4>Cortex Activation Results</h4>

            <div className="cortex-summary-grid">
              <div className="cortex-summary-item">
                <span className="cortex-summary-label">Fired Nodes</span>
                <span className="cortex-summary-value">{cr.fired_nodes ?? '—'}</span>
              </div>
              <div className="cortex-summary-item">
                <span className="cortex-summary-label">Dominant Route</span>
                <span className="cortex-summary-value">{route}</span>
              </div>
              <div className="cortex-summary-item">
                <span className="cortex-summary-label">Answer Score</span>
                <span className="cortex-summary-value">{cr.answer_score != null ? cr.answer_score.toFixed(2) : '—'}</span>
              </div>
              <div className="cortex-summary-item">
                <span className="cortex-summary-label">Engines Selected</span>
                <span className="cortex-summary-value">{engines.length}</span>
              </div>
            </div>

            <div className="cortex-capabilities">
              <h5>Ranked Capabilities</h5>
              {sortedCaps.map(([capId, score]) => {
                const pct = maxScore > 0 ? (score / maxScore) * 100 : 0;
                const isActive = engines.includes(capId);
                return (
                  <div key={capId} className={`cortex-cap-row ${isActive ? 'active' : 'inactive'}`}>
                    <span className="cap-name">{capId.replace(/^cap_/, '').replace(/_/g, ' ')}</span>
                    <div className="cap-bar-container">
                      <div className="cap-bar" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="cap-score">{score.toFixed(1)}</span>
                    {isActive && <span className="cap-active-badge">active</span>}
                  </div>
                );
              })}
            </div>

            {engines.length > 0 && (
              <div className="cortex-engines">
                <h5>Engines to Run</h5>
                <div className="engine-tags">
                  {engines.map(e => (
                    <span key={e} className="engine-tag">{e.replace(/^cap_/, '').replace(/_/g, ' ')}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Fired Subgraph Visualization */}
            {result.outputs_produced.activation_graph && (() => {
              const ag = result.outputs_produced.activation_graph;
              return (
                <div className="cortex-fired-graph">
                  <h5>
                    Expertise Graph Activation
                    <span className="cortex-graph-caption">
                      Showing top {ag.showing} of {ag.total_fired} fired nodes
                    </span>
                  </h5>
                  <div className="cortex-graph-container">
                    <ActivationGraph
                      seedNodes={ag.seed_nodes || []}
                      firedNodes={ag.fired_nodes || []}
                      synapses={ag.synapses || []}
                      width={760}
                      height={460}
                    />
                  </div>
                </div>
              );
            })()}
          </div>
        );
      })()}

      {/* Dynamic Cortex Execution Results */}
      {isDynamicCortexExecutor && (() => {
        const de = result.outputs_produced.dynamic_execution;
        const eo = result.outputs_produced.engine_outputs || {};
        const completed = de.nodes_completed || [];
        const failed = de.nodes_failed || [];
        const total = de.total_nodes || 0;

        const nodeEntries = Object.entries(eo).map(([nid, out]) => {
          const desc = (out.evaluation || out.engine_result?.status || '');
          const conf = out.confidence;
          const applicable = out.applicable;
          return { nid, out, desc, conf, applicable };
        });

        return (
          <div className="detail-section cortex-activation-detail">
            <h4>Dynamic Cortex Execution</h4>

            <div className="cortex-summary-grid">
              <div className="cortex-summary-item">
                <span className="cortex-summary-label">Nodes Assembled</span>
                <span className="cortex-summary-value">{total}</span>
              </div>
              <div className="cortex-summary-item">
                <span className="cortex-summary-label">Completed</span>
                <span className="cortex-summary-value" style={{ color: '#22c55e' }}>{completed.length}</span>
              </div>
              <div className="cortex-summary-item">
                <span className="cortex-summary-label">Failed</span>
                <span className="cortex-summary-value" style={{ color: failed.length > 0 ? '#ef4444' : '#94a3b8' }}>{failed.length}</span>
              </div>
              <div className="cortex-summary-item">
                <span className="cortex-summary-label">Outputs</span>
                <span className="cortex-summary-value">{nodeEntries.length}</span>
              </div>
            </div>

            {nodeEntries.length > 0 && (
              <div className="dynamic-node-results">
                <h5>Node Evaluations</h5>
                {nodeEntries.map(({ nid, out, desc, conf, applicable }) => (
                  <div key={nid} className={`dynamic-node-row ${applicable === false ? 'not-applicable' : ''}`}>
                    <div className="dynamic-node-header">
                      <span className="dynamic-node-id">{nid.substring(0, 12)}…</span>
                      {conf != null && (
                        <span className="dynamic-node-confidence" title="Confidence">
                          {(conf * 100).toFixed(0)}%
                        </span>
                      )}
                      {applicable === false && <span className="dynamic-node-badge na">N/A</span>}
                      {applicable === true && <span className="dynamic-node-badge ok">✓</span>}
                    </div>
                    <div className="dynamic-node-eval">{typeof desc === 'string' ? desc : JSON.stringify(desc).substring(0, 200)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Inputs Used */}
      <div className="detail-section collapsible">
        <div className="section-header" onClick={() => toggleSection('inputs')}>
          <h4>Inputs Used</h4>
          <span className="toggle-icon">{expandedSections.inputs ? '▼' : '▶'}</span>
        </div>
        {expandedSections.inputs && (
          <div className="section-content">
            {result?.inputs_used ? (
              <pre className="json-viewer">
                {JSON.stringify(result.inputs_used, null, 2)}
              </pre>
            ) : (
              <p className="no-data">No inputs recorded</p>
            )}
          </div>
        )}
      </div>

      {/* Outputs Produced */}
      <div className="detail-section collapsible">
        <div className="section-header" onClick={() => toggleSection('outputs')}>
          <h4>Outputs Produced</h4>
          <span className="toggle-icon">{expandedSections.outputs ? '▼' : '▶'}</span>
        </div>
        {expandedSections.outputs && (
          <div className="section-content">
            {result?.outputs_produced ? (
              <pre className="json-viewer">
                {JSON.stringify(result.outputs_produced, null, 2)}
              </pre>
            ) : (
              <p className="no-data">No outputs recorded</p>
            )}
          </div>
        )}
      </div>

      {/* Rendered Prompt (for LLM nodes) */}
      {isLLM && result?.prompt_rendered && (
        <div className="detail-section collapsible">
          <div className="section-header" onClick={() => toggleSection('prompt')}>
            <h4>Rendered Prompt</h4>
            <span className="toggle-icon">{expandedSections.prompt ? '▼' : '▶'}</span>
          </div>
          {expandedSections.prompt && (
            <div className="section-content">
              <pre className="prompt-viewer">{result.prompt_rendered}</pre>
            </div>
          )}
        </div>
      )}

      {/* LLM Response (for LLM nodes) */}
      {isLLM && result?.llm_response && (
        <div className="detail-section collapsible">
          <div className="section-header" onClick={() => toggleSection('response')}>
            <h4>LLM Response</h4>
            <span className="toggle-icon">{expandedSections.response ? '▼' : '▶'}</span>
          </div>
          {expandedSections.response && (
            <div className="section-content">
              <pre className="response-viewer">{result.llm_response}</pre>
            </div>
          )}
        </div>
      )}

      {/* Platform Details (for Platform pipeline nodes) */}
      {isSNN && result && (
        <div className="detail-section collapsible">
          <div className="section-header" onClick={() => toggleSection('snn')}>
            <h4>Platform Details</h4>
            <span className="toggle-icon">{expandedSections.snn ? '▼' : '▶'}</span>
          </div>
          {expandedSections.snn && (
            <div className="section-content">
              {snnInsight ? (
                <ExecutionInsightDetail insight={snnInsight} graphId={graphId} />
              ) : (
                <>
                  {result.snn_answer_frame && (
                    <div className="snn-section">
                      <h5>Answer</h5>
                      <pre className="json-viewer">
                        {JSON.stringify(result.snn_answer_frame, null, 2)}
                      </pre>
                    </div>
                  )}
                  {result.snn_top_outcome_nodes && (
                    <div className="snn-section">
                      <h5>Top Outcome Nodes</h5>
                      <pre className="json-viewer">
                        {JSON.stringify(result.snn_top_outcome_nodes, null, 2)}
                      </pre>
                    </div>
                  )}
                  {result.snn_evidence_paths && (
                    <div className="snn-section">
                      <h5>Evidence Paths</h5>
                      <pre className="json-viewer">
                        {JSON.stringify(result.snn_evidence_paths, null, 2)}
                      </pre>
                    </div>
                  )}
                  {result.snn_spike_stats && (
                    <div className="snn-section">
                      <h5>Selection Stats</h5>
                      <pre className="json-viewer">
                        {JSON.stringify(result.snn_spike_stats, null, 2)}
                      </pre>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Cross-Link Details */}
      {isCrossLink && crossLink && (
        <div className="detail-section">
          <h4>Cross-Link</h4>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Target Type</span>
              <span className="info-value">{crossLink.target_type}</span>
            </div>
            {(crossLink.target_process_name || crossLink.target_process_id) && (
              <div className="info-item">
                <span className="info-label">Target</span>
                <span className="info-value">
                  {crossLink.target_process_name || crossLink.target_process_id}
                </span>
              </div>
            )}
            {crossLink.child_status && (
              <div className="info-item">
                <span className="info-label">Child Status</span>
                <span className="info-value">{crossLink.child_status}</span>
              </div>
            )}
            {crossLink.child_duration_ms != null && (
              <div className="info-item">
                <span className="info-label">Child Duration</span>
                <span className="info-value">{crossLink.child_duration_ms}ms</span>
              </div>
            )}
            {crossLink.node_count != null && (
              <div className="info-item">
                <span className="info-label">Child Nodes</span>
                <span className="info-value">
                  {(crossLink.completed_nodes?.length ?? 0)} ok
                  {crossLink.failed_nodes?.length ? `, ${crossLink.failed_nodes.length} failed` : ''}
                </span>
              </div>
            )}
            {crossLink.depth != null && (
              <div className="info-item">
                <span className="info-label">Depth</span>
                <span className="info-value">{crossLink.depth}</span>
              </div>
            )}
          </div>

          {crossLink.child_persisted && crossLink.child_execution_id && (
            <div className="model-info" style={{ marginTop: '8px' }}>
              Sub-execution ID:{' '}
              <code title="Child process execution id (persisted with its own node results & history)">
                {crossLink.child_execution_id}
              </code>
            </div>
          )}

          {!crossLink.child_persisted && crossLink.child_node_results && (
            <details style={{ marginTop: '8px' }}>
              <summary style={{ cursor: 'pointer' }}>Child node results (stateless)</summary>
              <pre className="json-viewer">
                {JSON.stringify(crossLink.child_node_results, null, 2)}
              </pre>
            </details>
          )}
        </div>
      )}

      {/* Case Evidence Frame (case_extractor) */}
      {isCaseExtractor && caseEvidenceFrame && (
        <div className="detail-section collapsible">
          <div className="section-header" onClick={() => toggleSection('case_evidence')}>
            <h4>Case Evidence Frame</h4>
            <span className="toggle-icon">{expandedSections.case_evidence ? '▼' : '▶'}</span>
          </div>
          {expandedSections.case_evidence && (
            <div className="section-content">
              <CaseEvidenceFrameView frame={caseEvidenceFrame} />
            </div>
          )}
        </div>
      )}

      {/* Violations Report (chain_reasoner) */}
      {isChainReasoner && violationsReport && (
        <>
          <div className="detail-section">
            <AggregateDecisionCard report={violationsReport} title="Aggregate decision" />
          </div>
          <div className="detail-section collapsible">
            <div className="section-header" onClick={() => toggleSection('violations')}>
              <h4>Violations Report</h4>
              <span className="toggle-icon">{expandedSections.violations ? '▼' : '▶'}</span>
            </div>
            {expandedSections.violations && (
              <div className="section-content">
                <ViolationsReportView report={violationsReport} />
              </div>
            )}
          </div>
        </>
      )}

      {/* Error Details */}
      {result?.error && (
        <div className="detail-section error-section">
          <h4>Error Details</h4>
          <div className="error-content">
            {result.error_type && (
              <div className="error-type">Type: {result.error_type}</div>
            )}
            <div className="error-message">{result.error}</div>
          </div>
        </div>
      )}

      {/* History Events */}
      {historyEvents && historyEvents.length > 0 && (
        <div className="detail-section">
          <h4>Execution Events</h4>
          <div className="events-list">
            {historyEvents.map((event, idx) => (
              <div key={idx} className={`event-item ${event.action}`}>
                <span className="event-time">
                  {new Date(event.timestamp).toLocaleTimeString()}
                </span>
                <span className={`event-action ${event.action}`}>
                  {event.action.toUpperCase()}
                </span>
                {event.duration_ms && (
                  <span className="event-duration">{event.duration_ms}ms</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default NodeResultDetail;
